// Rich-text editor for email compose + signatures (TipTap).

import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import TextAlign from "@tiptap/extension-text-align";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import toast from "react-hot-toast";
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  List, ListOrdered, Link2, ImageIcon, Loader2,
  Undo2, Redo2, RemoveFormatting,
  AlignLeft, AlignCenter, AlignRight, LayoutTemplate,
} from "lucide-react";
import { resizeImageForEmail } from "../utils/signatureImage";
import { buildSignatureLayoutHtml, editorLooksEmpty } from "../utils/signatureLayoutTemplate";
import "./EmailComposeEditor.css";

function ToolbarBtn({ active, onClick, title, children, disabled, className = "" }) {
  return (
    <button
      type="button"
      className={`crm-compose-editor__btn${active ? " is-active" : ""}${className ? ` ${className}` : ""}`}
      title={title}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
}

function ToolbarDivider() {
  return <span className="crm-compose-editor__divider" aria-hidden />;
}

function EditorToolbar({
  editor,
  allowImages,
  imageBusy,
  onPickImage,
  imageInputRef,
  allowSignatureLayout,
  onInsertSignatureLayout,
}) {
  if (!editor) return null;

  const setLink = () => {
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("Paste link URL", prev || "https://");
    if (url === null) return;
    if (!url.trim()) {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    const href = url.trim().startsWith("http") ? url.trim() : `https://${url.trim()}`;
    editor.chain().focus().extendMarkRange("link").setLink({ href }).run();
  };

  return (
    <>
      {allowSignatureLayout && (
        <>
          <ToolbarBtn
            onClick={onInsertSignatureLayout}
            title="Professional layout"
            className="crm-compose-editor__btn--wide"
          >
            <LayoutTemplate size={15} />
            <span className="crm-compose-editor__btn-label">Layout</span>
          </ToolbarBtn>
          <ToolbarDivider />
        </>
      )}
      <ToolbarBtn
        active={editor.isActive("bold")}
        onClick={() => editor.chain().focus().toggleBold().run()}
        title="Bold (⌘B)"
      >
        <Bold size={15} />
      </ToolbarBtn>
      <ToolbarBtn
        active={editor.isActive("italic")}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        title="Italic (⌘I)"
      >
        <Italic size={15} />
      </ToolbarBtn>
      <ToolbarBtn
        active={editor.isActive("underline")}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        title="Underline (⌘U)"
      >
        <UnderlineIcon size={15} />
      </ToolbarBtn>
      <ToolbarBtn
        active={editor.isActive("strike")}
        onClick={() => editor.chain().focus().toggleStrike().run()}
        title="Strikethrough"
      >
        <Strikethrough size={15} />
      </ToolbarBtn>

      <ToolbarDivider />

      <ToolbarBtn
        active={editor.isActive({ textAlign: "left" })}
        onClick={() => editor.chain().focus().setTextAlign("left").run()}
        title="Align left"
      >
        <AlignLeft size={15} />
      </ToolbarBtn>
      <ToolbarBtn
        active={editor.isActive({ textAlign: "center" })}
        onClick={() => editor.chain().focus().setTextAlign("center").run()}
        title="Align center"
      >
        <AlignCenter size={15} />
      </ToolbarBtn>
      <ToolbarBtn
        active={editor.isActive({ textAlign: "right" })}
        onClick={() => editor.chain().focus().setTextAlign("right").run()}
        title="Align right"
      >
        <AlignRight size={15} />
      </ToolbarBtn>

      <ToolbarDivider />

      <ToolbarBtn
        active={editor.isActive("bulletList")}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        title="Bullet list"
      >
        <List size={15} />
      </ToolbarBtn>
      <ToolbarBtn
        active={editor.isActive("orderedList")}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        title="Numbered list"
      >
        <ListOrdered size={15} />
      </ToolbarBtn>
      <ToolbarBtn active={editor.isActive("link")} onClick={setLink} title="Insert link">
        <Link2 size={15} />
      </ToolbarBtn>
      {allowImages && (
        <ToolbarBtn
          onClick={() => imageInputRef.current?.click()}
          title="Insert image"
          disabled={imageBusy}
        >
          {imageBusy ? <Loader2 size={15} className="animate-spin" /> : <ImageIcon size={15} />}
        </ToolbarBtn>
      )}

      <ToolbarDivider />

      <ToolbarBtn
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
        title="Undo (⌘Z)"
      >
        <Undo2 size={15} />
      </ToolbarBtn>
      <ToolbarBtn
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
        title="Redo (⌘⇧Z)"
      >
        <Redo2 size={15} />
      </ToolbarBtn>
      <ToolbarBtn
        onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
        title="Clear formatting"
      >
        <RemoveFormatting size={15} />
      </ToolbarBtn>
    </>
  );
}

export default function EmailComposeEditor({
  value,
  onChange,
  placeholder,
  allowImages = true,
  minHeight = 200,
  variant = "signature",
  toolbarPosition = "top",
  autoFocus = false,
  imageMaxWidth,
}) {
  const imageInputRef = useRef(null);
  const imageBusyRef = useRef(false);
  const lastEmittedHtml = useRef(value || "");
  const [imageBusy, setImageBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const isCompose = variant === "compose";
  const allowSignatureLayout = !isCompose;
  const scrollClass = isCompose
    ? "crm-compose-editor__scroll crm-compose-editor__scroll--compose"
    : "crm-compose-editor__scroll crm-compose-editor__scroll--signature";

  const extensions = useMemo(() => {
    const base = [
      StarterKit.configure({ heading: false, codeBlock: false }),
      Underline,
      Link.configure({ openOnClick: false, autolink: true, linkOnPaste: true }),
      // Signature icons sit inline next to phone/email; compose keeps block images.
      Image.configure({ inline: !isCompose, allowBase64: true }),
      TextAlign.configure({ types: ["paragraph"] }),
    ];
    if (!isCompose) {
      base.push(
        Table.configure({
          resizable: false,
          HTMLAttributes: { style: "border-collapse:collapse;" },
        }),
        TableRow,
        TableHeader,
        TableCell.configure({
          HTMLAttributes: { style: "border:0;padding:0;vertical-align:top;" },
        }),
      );
    }
    return base;
  }, [isCompose]);

  const editor = useEditor({
    extensions,
    content: value || "",
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      lastEmittedHtml.current = html;
      onChange?.(html);
    },
    editorProps: {
      attributes: {
        "data-placeholder": placeholder || "Write your message…",
        class: "crm-compose-prosemirror",
        style: `min-height:${minHeight}px`,
      },
    },
  });

  useEffect(() => {
    if (!editor) return;
    const next = value || "";
    if (next === lastEmittedHtml.current) return;
    lastEmittedHtml.current = next;
    editor.commands.setContent(next, false);
  }, [editor, value]);

  useEffect(() => {
    if (!editor || !autoFocus) return;
    const t = setTimeout(() => editor.commands.focus("end"), 80);
    return () => clearTimeout(t);
  }, [editor, autoFocus]);

  const insertImageFile = useCallback(async (file) => {
    if (!file || !editor || imageBusyRef.current) return;
    if (!String(file.type || "").startsWith("image/")) {
      toast.error("Only image files can be dropped here");
      return;
    }
    imageBusyRef.current = true;
    setImageBusy(true);
    try {
      const opts = imageMaxWidth ? { maxWidth: imageMaxWidth } : {};
      const { dataUrl, width, bytes } = await resizeImageForEmail(file, opts);
      editor
        .chain()
        .focus()
        .insertContent([
          { type: "image", attrs: { src: dataUrl, width: String(width), alt: "" } },
          { type: "paragraph" },
        ])
        .focus("end")
        .run();
      lastEmittedHtml.current = editor.getHTML();
      onChange?.(lastEmittedHtml.current);
      toast.success(`Image added (${Math.round(bytes / 1024)} KB)`);
    } catch (err) {
      toast.error(err.message || "Image upload failed");
    } finally {
      imageBusyRef.current = false;
      setImageBusy(false);
    }
  }, [editor, imageMaxWidth, onChange]);

  const onPickImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    await insertImageFile(file);
  };

  const onInsertSignatureLayout = useCallback(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (!editorLooksEmpty(current)) {
      const ok = window.confirm("Replace the current signature with the professional layout?");
      if (!ok) return;
    }
    const html = buildSignatureLayoutHtml();
    editor.commands.setContent(html, false);
    lastEmittedHtml.current = editor.getHTML();
    onChange?.(lastEmittedHtml.current);
    toast.success("Layout added — edit text and replace the logo");
  }, [editor, onChange]);

  const onDragOver = (e) => {
    if (!allowImages) return;
    e.preventDefault();
    setDragOver(true);
  };

  const onDragLeave = () => setDragOver(false);

  const onDrop = async (e) => {
    if (!allowImages) return;
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    await insertImageFile(file);
  };

  if (!editor) {
    return (
      <div className="crm-compose-editor crm-compose-editor--loading" style={{ padding: 24, textAlign: "center" }}>
        <Loader2 size={18} className="animate-spin" style={{ opacity: 0.5 }} />
      </div>
    );
  }

  const toolbar = (
    <EditorToolbar
      editor={editor}
      allowImages={allowImages}
      imageBusy={imageBusy}
      onPickImage={onPickImage}
      imageInputRef={imageInputRef}
      allowSignatureLayout={allowSignatureLayout}
      onInsertSignatureLayout={onInsertSignatureLayout}
    />
  );

  return (
    <div
      className={`crm-compose-editor${isCompose ? " crm-compose-editor--message" : " crm-compose-editor--compact"}${dragOver ? " is-dragover" : ""}`}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {toolbarPosition === "top" && (
        <div className="crm-compose-editor__toolbar">{toolbar}</div>
      )}

      <div className={scrollClass} style={!isCompose ? { minHeight } : undefined}>
        <EditorContent editor={editor} />
      </div>

      {toolbarPosition === "bottom" && (
        <div className="crm-compose-editor__toolbar crm-compose-editor__toolbar--bottom">{toolbar}</div>
      )}

      <input
        ref={imageInputRef}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp"
        onChange={onPickImage}
        style={{ display: "none" }}
        aria-hidden
      />
    </div>
  );
}
